import { useFlags } from "launchdarkly-react-client-sdk";
import { ReactNode, useMemo } from "react";
import { useSelector } from "react-redux";

import { useAppSelector } from "app/hooks";
import { INBOX_UNREAD_COUNTS } from "constants/feature-flags";
import {
  selectCurrentClinic,
  selectCurrentPractice,
  selectCurrentRegionClinicsIds,
  selectCurrentUser,
  selectHasRegions
} from "features/auth/auth-slice";
import { useGlobalChat } from "features/chat/use-global-chat.hook";
import { getIncludeMessagesFromOtherClinicsState } from "features/inbox/inbox-slice";
import useTextRoutingCounts from "features/inbox/text-routing/use-text-routing-counts.hook";
import useDeferredReady from "hooks/use-deferred-ready.hook";
import useTextRoutingEnabled from "hooks/use-text-routing-enabled.hook";
import useTouchpointsCount from "hooks/use-touchpoints-count";
import { useGetUnreadFaxCountQuery } from "services/clinics";
import { useGetSuggestedCollectionsQuery } from "services/collections";
import { useGetUnreadMessagesCountQuery } from "services/messages";

import { MenuItemConfig } from "./menu";

export type BadgedMenuItem = MenuItemConfig & { badgeContent?: ReactNode };

/**
 * The side menu's badge counts — Inbox, Chat, Marketing, Collect, Faxes —
 * attached to the items this user can see. Visibility is `useVisibleMenuItems`'s
 * job; this hook only decorates.
 */
export default function useSideMenuBadges(
  visibleItems: MenuItemConfig[]
): BadgedMenuItem[] {
  const currentUser = useAppSelector(selectCurrentUser);
  const currentPractice = useAppSelector(selectCurrentPractice);
  const currentClinic = useAppSelector(selectCurrentClinic);
  const hasRegions = useAppSelector(selectHasRegions);
  const currentRegionClinicsIds = useAppSelector(selectCurrentRegionClinicsIds);
  const includeMessagesFromOtherClinics = useSelector(
    getIncludeMessagesFromOtherClinicsState
  );
  const { assignedTouchpointsCount } = useTouchpointsCount();
  const flags = useFlags();
  const { unreadCount } = useGlobalChat();
  const deferredReady = useDeferredReady();

  // Patient text routing: the Inbox badge counts what is waiting on ME, not
  // unread messages. Routing off => the unread query below runs exactly as before.
  // routingOn includes Inbox V2 (the routing views exist only there), so V1
  // keeps the unread badge.
  const { routingOn } = useTextRoutingEnabled();
  const { counts: routingCounts } = useTextRoutingCounts({
    skip: !deferredReady || !currentClinic?.id || !routingOn,
    pollingInterval: 60000
  });
  const { data: unreadMessagesCount } = useGetUnreadMessagesCountQuery(
    {
      clinicId: includeMessagesFromOtherClinics ? undefined : currentClinic?.id,
      clinics:
        includeMessagesFromOtherClinics && hasRegions
          ? currentRegionClinicsIds
          : undefined,
      byPatientClinic: Boolean(flags[INBOX_UNREAD_COUNTS])
    },
    {
      pollingInterval: 3600000,
      skip: !deferredReady || !currentClinic?.id
    }
  );

  const { data: unreadFaxCount } = useGetUnreadFaxCountQuery(
    { clinicId: currentClinic?.id! },
    {
      pollingInterval: 600000,
      skip: !deferredReady || !currentClinic?.id
    }
  );
  const { data: suggestedCollect } = useGetSuggestedCollectionsQuery(
    { limit: 0 },
    {
      pollingInterval: 600000,
      // Enrollment, not just the permission: a suggestion is an invitation to START a
      // collection, which is exactly what deactivation stops (ENG-1968). An unenrolled
      // practice has no settings row either, and the endpoint 500s without one.
      skip:
        !deferredReady ||
        !currentUser?.has_access_to_collections ||
        !currentPractice?.enrolled_in_collections
    }
  );

  return useMemo<BadgedMenuItem[]>(() => {
    return visibleItems.map((menuItem) => {
      if (menuItem.primary === "Inbox") {
        return {
          ...menuItem,
          badgeContent: routingOn
            ? routingCounts?.mine || 0
            : unreadMessagesCount || 0,
          // The badge changes meaning with routing on (waiting on me, not unread): say so.
          ...(routingOn
            ? {
                tooltip: `${menuItem.tooltip}. The badge counts conversations waiting on you`
              }
            : {})
        };
      }
      if (menuItem.primary === "Chat") {
        return { ...menuItem, badgeContent: unreadCount || 0 };
      }
      if (menuItem.primary === "Marketing") {
        return { ...menuItem, badgeContent: assignedTouchpointsCount };
      }
      if (menuItem.primary === "Collect") {
        return { ...menuItem, badgeContent: suggestedCollect?.count || 0 };
      }
      if (menuItem.primary === "Faxes") {
        return { ...menuItem, badgeContent: unreadFaxCount?.count || 0 };
      }
      return menuItem;
    });
  }, [
    visibleItems,
    unreadMessagesCount,
    routingOn,
    routingCounts?.mine,
    unreadCount,
    assignedTouchpointsCount,
    unreadFaxCount?.count,
    suggestedCollect?.count
  ]);
}
